/**
 * AvatarUploader — inline control for Profil.jsx (Vague 112).
 *
 * Flow:
 *   1. Click "Changer la photo" → opens a native file picker (PNG/JPEG/WebP).
 *   2. Preview + circular crop (square fit) inside a modal with zoom + drag.
 *   3. On "Enregistrer" → draw cropped region on a canvas, export to
 *      256×256 JPEG @ quality 0.85 (~25 KB), POST base64 to /api/me/avatar.
 *
 * The crop UX is kept deliberately simple (no external libs) so it works on
 * any browser: a fixed-ratio square viewport with pan (drag) and zoom (range
 * input). This mirrors the "good enough" pattern used in most SaaS admin
 * panels and avoids bundling react-easy-crop for a single-shot use case.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Upload, Camera, Loader2, Trash2, X } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const OUTPUT_SIZE = 256;
const OUTPUT_QUALITY = 0.85;
const ACCEPTED = 'image/png,image/jpeg,image/webp';

export default function AvatarUploader({ user, onUpdated }) {
  const fileRef = useRef(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const initials = (user?.name || user?.email || '?')
    .split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const onPickFile = () => fileRef.current?.click();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED.split(',').includes(file.type)) {
      toast({ title: 'Format non supporté', description: 'PNG, JPEG ou WebP uniquement.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Fichier trop lourd', description: '5 Mo max. Compressez ou choisissez une autre image.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLoadedSrc(ev.target?.result || null);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const saveAvatar = async (dataUri) => {
    setSaving(true);
    try {
      const { data } = await api.post('/me/avatar', { data_uri: dataUri });
      onUpdated?.(data);
      toast({ title: 'Photo de profil mise à jour' });
      setLoadedSrc(null);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Upload impossible' });
    } finally {
      setSaving(false);
    }
  };

  const removeAvatar = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete('/me/avatar');
      onUpdated?.(data);
      toast({ title: 'Photo supprimée' });
      setConfirmRemove(false);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Suppression impossible' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="avatar-uploader">
      <div className="relative">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow" data-testid="avatar-current" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#EEF2FF] text-[#4F46E5] font-bold text-[24px] flex items-center justify-center border-4 border-white shadow" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {initials}
          </div>
        )}
        <button
          type="button"
          onClick={onPickFile}
          data-testid="avatar-change-btn"
          title="Changer la photo"
          className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center shadow-lg hover:bg-[#4338CA] transition"
        >
          <Camera className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#1A1B2E] mb-1">Photo de profil</div>
        <div className="text-[12px] text-[#6B6C85] mb-2">PNG, JPEG ou WebP — recadrage carré automatique, ~25 Ko après compression.</div>
        <div className="flex gap-2">
          <button type="button" onClick={onPickFile} data-testid="avatar-upload-btn"
                  className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#E0E7FF]">
            <Upload className="w-3.5 h-3.5" /> Changer
          </button>
          {user?.picture && (
            <button type="button" onClick={() => setConfirmRemove(true)} data-testid="avatar-remove-btn"
                    className="h-9 px-3 rounded-lg border border-[#FECACA] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FEF2F2]">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
        </div>
      </div>
      <input ref={fileRef} type="file" accept={ACCEPTED} hidden onChange={handleFile} data-testid="avatar-file-input" />

      {loadedSrc && (
        <CropModal
          src={loadedSrc}
          saving={saving}
          onCancel={() => setLoadedSrc(null)}
          onSave={saveAvatar}
        />
      )}
      {confirmRemove && (
        <Dialog open={confirmRemove} onOpenChange={setConfirmRemove}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Supprimer la photo ?</DialogTitle>
              <DialogDescription>Vos initiales s'afficheront à la place.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button onClick={() => setConfirmRemove(false)} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">Annuler</button>
              <button onClick={removeAvatar} disabled={saving} data-testid="avatar-remove-confirm"
                      className="h-10 px-4 rounded-xl bg-[#DC2626] text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Crop modal — circular preview over a square viewport with pan + zoom. */
function CropModal({ src, saving, onCancel, onSave }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [img, setImg] = useState(null);
  const [drag, setDrag] = useState(null);
  const containerRef = useRef(null);
  const VIEW = 280; // viewport size (px)

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      setImg(image);
      // Fit image so its short edge = viewport, then center.
      const scale = VIEW / Math.min(image.width, image.height);
      setZoom(scale);
      setOffset({
        x: (VIEW - image.width * scale) / 2,
        y: (VIEW - image.height * scale) / 2,
      });
    };
    image.src = src;
  }, [src]);

  const onPointerDown = (e) => {
    setDrag({ sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    setOffset({ x: drag.ox + (e.clientX - drag.sx), y: drag.oy + (e.clientY - drag.sy) });
  };
  const onPointerUp = () => setDrag(null);

  const onZoomChange = (e) => {
    const newZoom = Number(e.target.value);
    if (!img) return setZoom(newZoom);
    // Keep the image centered around the current offset when zooming
    const cx = VIEW / 2 - offset.x;
    const cy = VIEW / 2 - offset.y;
    const ratio = newZoom / zoom;
    setZoom(newZoom);
    setOffset({ x: VIEW / 2 - cx * ratio, y: VIEW / 2 - cy * ratio });
  };

  const exportAndSave = () => {
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    // Map viewport coords (offset, zoom) onto output canvas
    const scaleOut = OUTPUT_SIZE / VIEW;
    ctx.drawImage(
      img,
      offset.x * scaleOut,
      offset.y * scaleOut,
      img.width * zoom * scaleOut,
      img.height * zoom * scaleOut,
    );
    const dataUri = canvas.toDataURL('image/jpeg', OUTPUT_QUALITY);
    onSave(dataUri);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Recadrez votre photo</DialogTitle>
          <DialogDescription>Déplacez l'image et ajustez le zoom pour centrer votre visage.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div
            ref={containerRef}
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={(e) => onPointerDown(e.touches[0])}
            onTouchMove={(e) => onPointerMove(e.touches[0])}
            onTouchEnd={onPointerUp}
            className="relative bg-[#1A1B2E] overflow-hidden select-none"
            style={{ width: VIEW, height: VIEW, borderRadius: 12, cursor: drag ? 'grabbing' : 'grab', touchAction: 'none' }}
            data-testid="avatar-crop-viewport"
          >
            {img && (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: offset.x, top: offset.y,
                  width: img.width * zoom,
                  height: img.height * zoom,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* Circular mask overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, transparent ${VIEW / 2 - 4}px, rgba(0,0,0,.55) ${VIEW / 2 - 2}px)`,
                borderRadius: 12,
              }}
            />
            <div
              className="absolute pointer-events-none"
              style={{
                left: 2, top: 2, width: VIEW - 4, height: VIEW - 4,
                borderRadius: '50%', boxShadow: 'inset 0 0 0 2px rgba(255,255,255,.8)',
              }}
            />
          </div>
          <div className="w-full flex items-center gap-2.5">
            <span className="text-[11px] text-[#8E8FA5]">Zoom</span>
            <input
              type="range"
              min={0.2} max={4} step={0.01}
              value={zoom} onChange={onZoomChange}
              data-testid="avatar-crop-zoom"
              className="flex-1"
            />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onCancel} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">
            <X className="w-4 h-4 inline mr-1" /> Annuler
          </button>
          <button onClick={exportAndSave} disabled={saving || !img} data-testid="avatar-crop-save"
                  className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
